import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { CloudArrowUp, CircleNotch, ShieldCheck } from '@phosphor-icons/react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * One-click migration: copies every disk-only attachment (legacy uploads)
 * into MongoDB GridFS so they survive container redeploys.
 * Idempotent — re-runs are no-ops.
 */
export default function AttachmentsMigrationCard({ canEdit }) {
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const handleMigrate = async () => {
    if (!canEdit) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/documents/attachments/migrate-to-gridfs`);
      setLastResult(res.data);
      const { migrated, skipped_disk_missing, failed } = res.data || {};
      if (migrated > 0) {
        toast.success(`Migrated ${migrated} attachment(s) to MongoDB`);
      } else {
        toast.info('All attachments already migrated');
      }
      if (failed > 0) toast.warning(`${failed} attachment(s) failed to migrate — see logs`);
      if (skipped_disk_missing > 0) toast.warning(`${skipped_disk_missing} attachment(s) had no disk copy and could not be migrated`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Migration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card border rounded-2xl p-6 space-y-4" data-testid="attachments-migration-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-700 dark:text-blue-400 flex items-center justify-center">
          <CloudArrowUp size={20} weight="duotone" />
        </div>
        <div>
          <h3 className="font-heading font-semibold flex items-center gap-2">
            Attachment Storage <ShieldCheck size={14} className="text-emerald-600" weight="fill" />
          </h3>
          <p className="text-xs text-muted-foreground">
            Make uploaded attachments survive container redeploys by copying them into MongoDB.
          </p>
        </div>
      </div>

      <div className="rounded-lg bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
        New uploads go to MongoDB automatically. Run this once to back-fill any legacy
        attachments uploaded before this feature was enabled.
      </div>

      {lastResult && (
        <div className="rounded-lg border bg-emerald-500/5 border-emerald-500/30 px-3 py-2 text-xs">
          Last run — migrated <strong>{lastResult.migrated}</strong>, skipped (no disk copy){' '}
          <strong>{lastResult.skipped_disk_missing}</strong>, failed <strong>{lastResult.failed}</strong>.
        </div>
      )}

      {lastResult && lastResult.broken && lastResult.broken.length > 0 && (
        <div className="rounded-lg border bg-amber-500/5 border-amber-500/40 px-3 py-3 text-xs space-y-2">
          <div className="font-semibold text-amber-700 dark:text-amber-400">
            ⚠️ {lastResult.broken.length} attachment(s) have lost their file bytes
          </div>
          <p className="text-muted-foreground">
            These were uploaded before the durable-storage feature shipped, and the
            container disk was wiped during a redeploy. Please re-upload them on the
            corresponding documents — going forward all new uploads are redeploy-safe.
          </p>
          <div className="max-h-48 overflow-y-auto bg-background/50 rounded-md p-2 space-y-1">
            {lastResult.broken.map((b) => (
              <div key={b.id} className="flex items-center gap-2 text-[11px]">
                <span className="font-mono text-amber-700 dark:text-amber-400">{b.document_type}</span>
                <span className="font-medium">{b.original_filename}</span>
                <span className="text-muted-foreground truncate">→ {b.document_id?.slice(0, 8)}…</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={handleMigrate}
          disabled={!canEdit || loading}
          data-testid="migrate-attachments-btn"
        >
          {loading ? <CircleNotch className="mr-2 animate-spin" size={16} /> : <CloudArrowUp className="mr-2" size={16} />}
          Migrate Attachments to MongoDB
        </Button>
      </div>
    </div>
  );
}
